import { AlertCircle, CheckCircle2, Clock, UserCog } from "lucide-react";
import { ClientAvatar } from "@/components/ClientAvatar";
import { DeliverableBadge, DeliverableStatusBadge } from "@/components/Badges";
import { formatDayLabel } from "@/lib/constants";

const groupByOrder = (items) => {
  const map = new Map();
  items.forEach((d) => {
    if (!map.has(d.order_id)) map.set(d.order_id, []);
    map.get(d.order_id).push(d);
  });
  return [...map.values()];
};

const DueFlag = ({ d, tomorrow }) => (d.overdue ? (
  <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-medium text-rose-400" data-testid={`today-due-flag-${d.deliverable_id}`}>
    <AlertCircle className="h-3 w-3 shrink-0" />{formatDayLabel(d.due_date, { month: "short", day: "numeric" })}
  </span>
) : (
  <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-medium text-amber-400" data-testid={`today-due-flag-${d.deliverable_id}`}>
    <Clock className="h-3 w-3 shrink-0" />{tomorrow ? "Tomorrow" : "Today"}
  </span>
));

const DeliverBtn = ({ d, onDeliver }) => (
  <button onClick={(e) => { e.stopPropagation(); onDeliver(d.order_id, d.deliverable_id); }} title="Mark delivered"
    data-testid={`today-due-deliver-${d.deliverable_id}`}
    className="flex w-10 shrink-0 items-center justify-center border-l border-border bg-background/40 text-muted-foreground transition-colors hover:bg-emerald-500/20 hover:text-emerald-500">
    <CheckCircle2 className="h-5 w-5" />
  </button>
);

const ProductLine = ({ d }) => (
  <div className="flex min-w-0 flex-wrap items-center gap-1 whitespace-nowrap" data-testid={`today-due-line-${d.deliverable_id}`}>
    <span className="inline-flex min-w-0 max-w-full overflow-hidden [&>*]:block [&>*]:max-w-full [&>*]:truncate" title={d.type}><DeliverableBadge type={d.type} /></span>
    {d.editor_name ? (
      <span className="editor-bubble inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
        style={{ "--editor-color": d.editor_color }} data-testid={`today-due-editor-${d.deliverable_id}`}>
        <UserCog className="h-2.5 w-2.5 shrink-0" /><span>{d.editor_name}</span>
      </span>
    ) : (
      <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-dashed border-muted-foreground/40 px-1.5 py-0.5 text-[10px] text-muted-foreground"
        data-testid={`today-due-unassigned-${d.deliverable_id}`}>
        <UserCog className="h-2.5 w-2.5" />Unassigned
      </span>
    )}
    <span data-testid={`today-due-status-${d.deliverable_id}`} className="inline-flex shrink-0"><DeliverableStatusBadge status={d.status} /></span>
  </div>
);

const Header = ({ d, right }) => (
  <div className="flex items-center justify-between gap-2">
    <div className="min-w-0">
      <div className="truncate text-sm font-medium">{d.address}</div>
      {d.client_name && (
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground" data-testid={`today-due-client-${d.order_id}`}>
          <ClientAvatar name={d.client_name} className="h-4 w-4 text-[7px]" /><span className="truncate">{d.client_name}</span>
        </div>
      )}
    </div>
    {right}
  </div>
);

const ProductRow = ({ d, tomorrow, onDeliver }) => (
  <div className="flex items-stretch border-t border-border/60" data-testid={`today-due-${d.deliverable_id}`}>
    <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-2 gap-y-1 py-1.5 pl-3 pr-2">
      <ProductLine d={d} />
      <DueFlag d={d} tomorrow={tomorrow} />
    </div>
    <DeliverBtn d={d} onDeliver={onDeliver} />
  </div>
);

const SingleRow = ({ d, tomorrow, onDeliver }) => (
  <div className="flex items-stretch" data-testid={`today-due-${d.deliverable_id}`}>
    <div className="min-w-0 flex-1 space-y-1.5 px-3 py-2">
      <Header d={d} />
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <ProductLine d={d} />
        <DueFlag d={d} tomorrow={tomorrow} />
      </div>
    </div>
    <DeliverBtn d={d} onDeliver={onDeliver} />
  </div>
);

export const DueGroups = ({ items, tomorrow, onOpen, onDeliver }) => (
  <div className="space-y-2">
    {groupByOrder(items).map((group) => {
      const first = group[0];
      const overdue = group.some((d) => d.overdue);
      return (
        <div key={first.order_id} onClick={() => onOpen(first.order_id)} data-testid={`today-due-group-${first.order_id}`}
          className={`relative cursor-pointer overflow-hidden rounded-lg border bg-card transition-colors ${overdue ? "border-rose-500/40 hover:border-rose-500/60" : "border-border hover:border-primary/40"}`}>
          <div aria-hidden className={`pointer-events-none absolute inset-0 ${overdue ? "bg-rose-500/10" : "bg-secondary/30"}`} />
          {group.length === 1 ? (
            <div className="relative"><SingleRow d={first} tomorrow={tomorrow} onDeliver={onDeliver} /></div>
          ) : (
          <div className="relative">
            <div className="px-3 py-2">
              <Header d={first} right={
                <span className={`shrink-0 whitespace-nowrap text-[11px] font-semibold ${overdue ? "text-rose-400" : "text-amber-400"}`} data-testid={`today-due-group-count-${first.order_id}`}>
                  {overdue ? "Overdue" : tomorrow ? "Due tomorrow" : "Due today"} · {group.length}
                </span>
              } />
            </div>
            {group.map((d) => <ProductRow key={d.deliverable_id} d={d} tomorrow={tomorrow} onDeliver={onDeliver} />)}
          </div>
          )}
        </div>
      );
    })}
  </div>
);
